import { differenceInCalendarDays, parseISO } from 'date-fns'
import type {
  Attendance,
  AttendanceStatus,
  PopulatedShiftAssignmentRef,
} from '@/features/attendance/types/attendance.types'

export const ATTENDANCE_STATUS_LABEL: Record<AttendanceStatus, string> = {
  present: 'Present',
  absent: 'Absent',
  late: 'Late',
  'half-day': 'Half-day',
  'on-leave': 'On leave',
}

/** "9h 08m" — minutes padded so a column of durations lines up. */
export function formatWorkedMinutes(minutes: number | undefined | null): string {
  if (minutes == null || minutes < 0) return '—'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}m`
  return `${h}h ${String(m).padStart(2, '0')}m`
}

/** A session is live when it's flagged active, or (older records) has an open clock-in. */
export function isCurrentlyClockedIn(row: Attendance): boolean {
  return row.isActive === true || (!!row.clockIn && !row.clockOut)
}

/** Calendar days between clock-in and clock-out, for the "+1d" marker on overnight shifts. */
export function dayOffset(clockIn?: string, clockOut?: string): number {
  if (!clockIn || !clockOut) return 0
  return differenceInCalendarDays(parseISO(clockOut), parseISO(clockIn))
}

export function attendanceUserName(row: Attendance): string {
  const full = [row.user?.firstName, row.user?.lastName]
    .map((p) => p?.trim())
    .filter(Boolean)
    .join(' ')
  return full || row.user?.email || 'Unknown employee'
}

/** Offset (ms) of `timeZone` from UTC at `instant`. */
function zoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant)
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value)
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'))
  return asUtc - instant.getTime()
}

/** The instant a wall-clock `HH:mm` on `yyyy-MM-dd` happens in `timeZone` (browser zone if omitted). */
export function zonedWallTimeToDate(date: string, hhmm: string, timeZone?: string): Date | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  const t = /^(\d{1,2}):(\d{2})$/.exec(hhmm)
  if (!d || !t) return null
  const [y, mo, day, h, mi] = [d[1], d[2], d[3], t[1], t[2]].map(Number)
  if (!timeZone) return new Date(y, mo - 1, day, h, mi)
  try {
    const guess = Date.UTC(y, mo - 1, day, h, mi)
    // Second pass settles DST transitions where the first offset is off by an hour.
    const first = guess - zoneOffsetMs(new Date(guess), timeZone)
    return new Date(guess - zoneOffsetMs(new Date(first), timeZone))
  } catch {
    return null
  }
}

export interface ScheduledWindow {
  start: Date
  end: Date
  startTime: string
  endTime: string
  overnight: boolean
}

export function getAssignmentRef(row: Attendance): PopulatedShiftAssignmentRef | null {
  const ref = row.shiftAssignmentId
  return ref != null && typeof ref === 'object' ? ref : null
}

/** The shift the attendance was clocked against, as real instants. Null for unscheduled sessions. */
export function getScheduledWindow(row: Attendance): ScheduledWindow | null {
  const assignment = getAssignmentRef(row)
  const startTime = assignment?.startTime
  const endTime = assignment?.endTime
  const date = (assignment?.date ?? row.date)?.slice(0, 10)
  if (!startTime || !endTime || !date) return null
  const start = zonedWallTimeToDate(date, startTime, row.timezone)
  let end = zonedWallTimeToDate(date, endTime, row.timezone)
  if (!start || !end) return null
  const overnight = end.getTime() <= start.getTime()
  if (overnight) end = new Date(end.getTime() + 24 * 60 * 60 * 1000)
  return { start, end, startTime, endTime, overnight }
}
